import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { toast } from "sonner";
import { 
  Code, 
  GitBranch, 
  GitCommit, 
  GitPullRequest, 
  Search,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  Calendar,
  User,
  PlusCircle,
  Pencil,
  Trash
} from "lucide-react";
import { 
  fetchFeatureImplementations, 
  getImplementationStatusCounts,
  fetchImplementationById,
  createImplementation,
  updateImplementation,
  fetchTasksByImplementationId,
  createTask,
  updateTask,
  deleteTask
} from "@/lib/services/feature-implementation-service";
import { fetchFeatureRequests } from "@/lib/services/feature-request-service";
import type { 
  FeatureImplementation, 
  ImplementationTask, 
  FeatureRequest,
  ImplementationStatusCounts 
} from "@/lib/types/feature-management";
import { useAuth } from "@/lib/context/auth-context";
import { format, parseISO } from "date-fns";

export default function FeatureImplementation() {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedImplementation, setSelectedImplementation] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [isNewImplementationOpen, setIsNewImplementationOpen] = useState(false);
  const [isNewTaskOpen, setIsNewTaskOpen] = useState(false);
  const [isEditTaskOpen, setIsEditTaskOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<ImplementationTask | null>(null);
  const [implementationForm, setImplementationForm] = useState({
    title: "",
    description: "",
    technical_details: "",
    status: "planning",
    priority: "medium",
    assigned_to: "",
    estimated_completion: "",
    git_branch: "",
    related_request: ""
  });
  const [taskForm, setTaskForm] = useState<{
    title: string;
    status: "not-started" | "in-progress" | "completed" | "blocked";
    assigned_to: string;
    due_date: string;
    notes: string;
  }>({
    title: "",
    status: "not-started",
    assigned_to: "",
    due_date: "",
    notes: ""
  });
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  const { 
    data: implementations = [], 
    isLoading: isLoadingImplementations 
  } = useQuery({
    queryKey: ['feature-implementations'],
    queryFn: fetchFeatureImplementations
  });

  const { 
    data: statusCounts = { planning: 0, inProgress: 0, testing: 0, completed: 0 },
    isLoading: isLoadingCounts
  } = useQuery({
    queryKey: ['implementation-counts'],
    queryFn: getImplementationStatusCounts
  });

  const { 
    data: featureRequests = [],
    isLoading: isLoadingRequests
  } = useQuery({
    queryKey: ['feature-requests-for-select'],
    queryFn: fetchFeatureRequests
  });

  const {
    data: implementationDetails,
    isLoading: isLoadingDetails,
    refetch: refetchDetails
  } = useQuery({
    queryKey: ['implementation-details', selectedImplementation],
    queryFn: () => selectedImplementation ? fetchImplementationById(selectedImplementation) : null,
    enabled: !!selectedImplementation
  });

  const {
    data: implementationTasks = [],
    isLoading: isLoadingTasks,
    refetch: refetchTasks
  } = useQuery({
    queryKey: ['implementation-tasks', selectedImplementation],
    queryFn: () => selectedImplementation ? fetchTasksByImplementationId(selectedImplementation) : Promise.resolve([]),
    enabled: !!selectedImplementation
  });

  const createImplementationMutation = useMutation({
    mutationFn: (data: any) => createImplementation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-implementations'] });
      queryClient.invalidateQueries({ queryKey: ['implementation-counts'] });
      setIsNewImplementationOpen(false);
      resetImplementationForm();
    }
  });

  const createTaskMutation = useMutation({
    mutationFn: (data: any) => createTask(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['implementation-tasks', selectedImplementation] });
      setIsNewTaskOpen(false);
      resetTaskForm();
    }
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string, updates: Partial<ImplementationTask> }) => updateTask(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['implementation-tasks', selectedImplementation] });
      setIsEditTaskOpen(false);
      setSelectedTask(null);
    }
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (id: string) => deleteTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['implementation-tasks', selectedImplementation] });
    }
  });

  const updateImplementationStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string, status: string }) => 
      updateImplementation(id, { status: status as FeatureImplementation['status'] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-implementations'] });
      queryClient.invalidateQueries({ queryKey: ['implementation-counts'] });
      queryClient.invalidateQueries({ queryKey: ['implementation-details', selectedImplementation] });
    }
  });

  const filteredImplementations = implementations.filter(implementation => {
    const matchesSearch = 
      implementation.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      implementation.id.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || implementation.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const resetImplementationForm = () => {
    setImplementationForm({
      title: "",
      description: "",
      technical_details: "",
      status: "planning",
      priority: "medium",
      assigned_to: "",
      estimated_completion: "",
      git_branch: "",
      related_request: ""
    });
  };

  const resetTaskForm = () => {
    setTaskForm({
      title: "",
      status: "not-started",
      assigned_to: "",
      due_date: "",
      notes: ""
    });
  };

  const handleCreateImplementation = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!implementationForm.title) {
      toast.error("Title is required");
      return;
    }

    const newImplementation = {
      ...implementationForm,
      progress: 0
    };

    createImplementationMutation.mutate(newImplementation);
  };

  const handleCreateTask = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!taskForm.title || !selectedImplementation) {
      toast.error("Task title is required");
      return;
    }

    const newTask = {
      ...taskForm,
      implementation_id: selectedImplementation
    };

    createTaskMutation.mutate(newTask);
  };

  const handleEditTask = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!taskForm.title || !selectedTask) {
      toast.error("Task title is required");
      return;
    }

    updateTaskMutation.mutate({
      id: selectedTask.id,
      updates: taskForm
    });
  };

  const handleDeleteTask = (taskId: string) => {
    if (confirm("Are you sure you want to delete this task?")) {
      deleteTaskMutation.mutate(taskId);
    }
  };

  const handleUpdateStatus = (status: string) => {
    if (!selectedImplementation) return;
    
    updateImplementationStatusMutation.mutate({
      id: selectedImplementation,
      status
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "planning":
        return <Badge variant="secondary" className="font-subtitle">Planning</Badge>;
      case "in-progress":
        return <Badge variant="primary-subtle" className="font-subtitle">In Progress</Badge>;
      case "testing":
        return <Badge variant="warning" className="bg-amber-500 font-subtitle">Testing</Badge>;
      case "completed":
        return <Badge variant="success" className="bg-green-500 font-subtitle">Completed</Badge>;
      case "blocked":
        return <Badge variant="destructive" className="font-subtitle">Blocked</Badge>;
      default:
        return <Badge className="font-subtitle">{status}</Badge>;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "high":
        return <Badge variant="destructive" className="bg-red-500 font-subtitle">High</Badge>;
      case "medium":
        return <Badge variant="accent" className="font-subtitle">Medium</Badge>;
      case "low":
        return <Badge variant="secondary" className="font-subtitle">Low</Badge>;
      default:
        return <Badge className="font-subtitle">{priority}</Badge>;
    }
  };

  const getTaskStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case "in-progress":
        return <Clock className="h-4 w-4 text-blue-500" />;
      case "not-started":
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
      case "blocked":
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch (error) {
      return dateString;
    }
  };

  const setupTaskForEdit = (task: ImplementationTask) => {
    setSelectedTask(task);
    setTaskForm({
      title: task.title,
      status: task.status,
      assigned_to: task.assigned_to || "",
      due_date: task.due_date || "",
      notes: task.notes || ""
    });
    setIsEditTaskOpen(true);
  };

  const isStaffOrAdmin = profile?.role === 'staff' || profile?.role === 'admin' || profile?.role === 'superadmin' || profile?.role === 'tech';

  return (
    <div className="container mx-auto pt-6 px-6 space-y-6 max-w-7xl animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-title font-bold tracking-tight text-primary">Feature Implementation</h1>
          <p className="text-muted-foreground font-subtitle">Track and manage implementation of feature requests</p>
        </div>
        {isStaffOrAdmin && (
          <Button 
            className="bg-accent hover:bg-accent/90"
            onClick={() => setIsNewImplementationOpen(true)}
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            New Implementation
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white shadow-sm border-l-4 border-l-gray-500">
          <CardContent className="p-6 flex flex-col items-center justify-center">
            <Code className="h-8 w-8 text-gray-500 mb-2" />
            <CardTitle className="text-xl mb-1 font-title">Planning</CardTitle>
            <p className="text-3xl font-bold">{isLoadingCounts ? '...' : statusCounts.planning}</p>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm border-l-4 border-l-primary">
          <CardContent className="p-6 flex flex-col items-center justify-center">
            <GitBranch className="h-8 w-8 text-primary mb-2" />
            <CardTitle className="text-xl mb-1 font-title">In Progress</CardTitle>
            <p className="text-3xl font-bold">{isLoadingCounts ? '...' : statusCounts.inProgress}</p>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm border-l-4 border-l-amber-500">
          <CardContent className="p-6 flex flex-col items-center justify-center">
            <GitCommit className="h-8 w-8 text-amber-500 mb-2" />
            <CardTitle className="text-xl mb-1 font-title">Testing</CardTitle>
            <p className="text-3xl font-bold">{isLoadingCounts ? '...' : statusCounts.testing}</p>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm border-l-4 border-l-green-500">
          <CardContent className="p-6 flex flex-col items-center justify-center">
            <GitPullRequest className="h-8 w-8 text-green-500 mb-2" />
            <CardTitle className="text-xl mb-1 font-title">Completed</CardTitle>
            <p className="text-3xl font-bold">{isLoadingCounts ? '...' : statusCounts.completed}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5">
          <Card className="rounded-lg border border-accent-light/30 bg-white shadow-sm">
            <CardHeader className="bg-accent-subtle/20 border-b border-accent-light/30 p-4">
              <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
                <CardTitle className="text-xl font-bold font-title text-primary">Implementations</CardTitle>
                <div className="flex flex-col space-y-2 md:flex-row md:space-x-2 md:space-y-0">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-8 w-full md:w-[200px] font-subtitle"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm font-subtitle"
                  >
                    <option value="all">All Status</option>
                    <option value="planning">Planning</option>
                    <option value="in-progress">In Progress</option>
                    <option value="testing">Testing</option>
                    <option value="completed">Completed</option>
                    <option value="blocked">Blocked</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-accent-light/20 max-h-[600px] overflow-y-auto">
                {isLoadingImplementations ? (
                  <div className="flex justify-center items-center p-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    <span className="ml-2">Loading implementations...</span>
                  </div>
                ) : filteredImplementations.length === 0 ? (
                  <div className="p-8 text-center">
                    <p className="text-muted-foreground">No implementations found matching your filters</p>
                  </div>
                ) : (
                  filteredImplementations.map((implementation) => (
                    <div 
                      key={implementation.id}
                      className={`p-4 hover:bg-accent-subtle/10 cursor-pointer ${
                        selectedImplementation === implementation.id ? 'bg-accent-subtle/20' : ''
                      }`}
                      onClick={() => setSelectedImplementation(implementation.id)}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs font-medium text-muted-foreground">{implementation.id.substring(0, 8)}</span>
                            {getStatusBadge(implementation.status)}
                          </div>
                          <h3 className="font-title text-md font-bold mt-1">{implementation.title}</h3>
                        </div>
                        <div className="flex items-center gap-2">
                          {getPriorityBadge(implementation.priority)}
                        </div>
                      </div>
                      <p className="text-sm font-body line-clamp-2 mb-2">{implementation.description}</p>
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <User className="h-3.5 w-3.5" />
                          <span>{implementation.assigned_to || 'Unassigned'}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          <span>ETA: {formatDate(implementation.estimated_completion)}</span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-3">
                        <div 
                          className="h-1.5 rounded-full" 
                          style={{ 
                            width: `${implementation.progress}%`,
                            backgroundColor: 
                              implementation.progress < 25 ? '#ef4444' : 
                              implementation.progress < 50 ? '#f59e0b' :
                              implementation.progress < 75 ? '#3b82f6' : '#22c55e'
                          }} 
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-7">
          {!selectedImplementation ? (
            <div className="flex items-center justify-center h-full min-h-[400px] border rounded-lg bg-accent-subtle/5 border-dashed">
              <div className="text-center p-4">
                <GitBranch className="mx-auto h-12 w-12 text-muted-foreground opacity-50 mb-3" />
                <h3 className="text-lg font-title font-medium">No Implementation Selected</h3>
                <p className="text-muted-foreground max-w-xs mx-auto mt-2">
                  Select an implementation from the list to view its details and manage associated tasks.
                </p>
              </div>
            </div>
          ) : isLoadingDetails ? (
            <div className="flex justify-center items-center min-h-[400px]">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-2">Loading details...</span>
            </div>
          ) : implementationDetails && (
            <Card className="rounded-lg border border-accent-light/30 bg-white shadow-sm">
              <CardHeader className="bg-accent-subtle/20 border-b border-accent-light/30 p-4">
                <div className="flex flex-col space-y-2 md:flex-row md:items-center md:justify-between md:space-y-0">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-medium">{implementationDetails.id.substring(0, 8)}</span>
                      {getStatusBadge(implementationDetails.status)}
                    </div>
                    <CardTitle className="text-xl font-bold font-title text-primary mt-1">
                      {implementationDetails.title}
                    </CardTitle>
                  </div>
                  {isStaffOrAdmin && (
                    <div className="flex gap-2">
                      <Button variant="ghost">Edit</Button>
                      <Button onClick={() => setActiveTab("tasks")}>Manage Tasks</Button>
                    </div>
                  )}
                </div>
                
                <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
                  <TabsList className="font-subtitle">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="tasks">Tasks</TabsTrigger>
                    <TabsTrigger value="technical">Technical Details</TabsTrigger>
                  </TabsList>
                
                  <CardContent className="p-0">
                    <TabsContent value="overview" className="m-0">
                      <div className="p-4 space-y-4">
                        <div>
                          <h3 className="text-sm font-section font-medium text-muted-foreground mb-1">Description</h3>
                          <p className="font-body">{implementationDetails.description}</p>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h3 className="text-sm font-section font-medium text-muted-foreground mb-2">Details</h3>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-subtitle">Related Request:</span>
                                <span className="text-sm font-body font-medium">
                                  {implementationDetails.related_request ? 
                                    implementationDetails.related_request.substring(0, 8) : 'None'}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-subtitle">Priority:</span>
                                <span>{getPriorityBadge(implementationDetails.priority)}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-subtitle">Assigned Team:</span>
                                <span className="text-sm font-body font-medium">
                                  {implementationDetails.assigned_to || 'Unassigned'}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-subtitle">Last Updated:</span>
                                <span className="text-sm font-body">{formatDate(implementationDetails.last_updated)}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div>
                            <h3 className="text-sm font-section font-medium text-muted-foreground mb-2">Progress</h3>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-subtitle">Target Completion:</span>
                                <span className="text-sm font-body">{formatDate(implementationDetails.estimated_completion)}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-subtitle">Current Status:</span>
                                <span>{getStatusBadge(implementationDetails.status)}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-subtitle">Git Branch:</span>
                                <span className="text-sm font-mono bg-gray-100 px-2 py-0.5 rounded">
                                  {implementationDetails.git_branch || 'Not set'}
                                </span>
                              </div>
                              <div>
                                <div className="flex justify-between mb-1">
                                  <span className="text-sm font-subtitle">Completion:</span>
                                  <span className="text-sm font-body font-medium">{implementationDetails.progress}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="h-2 rounded-full" 
                                    style={{ 
                                      width: `${implementationDetails.progress}%`,
                                      backgroundColor: 
                                        implementationDetails.progress < 25 ? '#ef4444' : 
                                        implementationDetails.progress < 50 ? '#f59e0b' :
                                        implementationDetails.progress < 75 ? '#3b82f6' : '#22c55e'
                                    }} 
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {isStaffOrAdmin && (
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <h3 className="text-sm font-section font-medium text-muted-foreground mb-2">Update Status</h3>
                            <div className="flex flex-wrap gap-2">
                              <Button 
                                size="sm" 
                                variant={implementationDetails.status === 'planning' ? 'default' : 'outline'}
                                className={implementationDetails.status === 'planning' ? 'bg-gray-500' : ''}
                                onClick={() => handleUpdateStatus('planning')}
                              >
                                Planning
                              </Button>
                              <Button 
                                size="sm" 
                                variant={implementationDetails.status === 'in-progress' ? 'default' : 'outline'}
                                className={implementationDetails.status === 'in-progress' ? 'bg-primary' : ''}
                                onClick={() => handleUpdateStatus('in-progress')}
                              >
                                In Progress
                              </Button>
                              <Button 
                                size="sm" 
                                variant={implementationDetails.status === 'testing' ? 'default' : 'outline'}
                                className={implementationDetails.status === 'testing' ? 'bg-amber-500' : ''}
                                onClick={() => handleUpdateStatus('testing')}
                              >
                                Testing
                              </Button>
                              <Button 
                                size="sm" 
                                variant={implementationDetails.status === 'completed' ? 'default' : 'outline'}
                                className={implementationDetails.status === 'completed' ? 'bg-green-500' : ''}
                                onClick={() => handleUpdateStatus('completed')}
                              >
                                Completed
                              </Button>
                              <Button 
                                size="sm" 
                                variant={implementationDetails.status === 'blocked' ? 'default' : 'outline'}
                                className={implementationDetails.status === 'blocked' ? 'bg-destructive' : ''}
                                onClick={() => handleUpdateStatus('blocked')}
                              >
                                Blocked
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="tasks" className="m-0">
                      <div className="p-4">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-md font-title font-bold">Implementation Tasks</h3>
                          {isStaffOrAdmin && (
                            <Button 
                              size="sm" 
                              className="bg-accent hover:bg-accent/90"
                              onClick={() => setIsNewTaskOpen(true)}
                            >
                              <PlusCircle className="mr-2 h-4 w-4" />
                              Add Task
                            </Button>
                          )}
                        </div>
                        
                        <div className="space-y-3">
                          {isLoadingTasks ? (
                            <div className="flex justify-center py-8">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                            </div>
                          ) : implementationTasks.length > 0 ? (
                            implementationTasks.map(task => (
                              <Card key={task.id} className="border-accent-light/30">
                                <CardContent className="p-3">
                                  <div className="flex items-start justify-between">
                                    <div className="flex items-start gap-3">
                                      {getTaskStatusIcon(task.status)}
                                      <div>
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="font-mono text-xs">{task.id.substring(0, 8)}</span>
                                          {task.status === "completed" ? (
                                            <Badge variant="success" className="bg-green-500 font-subtitle">Completed</Badge>
                                          ) : task.status === "in-progress" ? (
                                            <Badge variant="primary-subtle" className="font-subtitle">In Progress</Badge>
                                          ) : task.status === "blocked" ? (
                                            <Badge variant="destructive" className="font-subtitle">Blocked</Badge>
                                          ) : (
                                            <Badge variant="secondary" className="font-subtitle">Not Started</Badge>
                                          )}
                                        </div>
                                        <h4 className="font-title font-medium">{task.title}</h4>
                                        <p className="text-sm text-muted-foreground mt-1">{task.notes}</p>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-subtitle text-muted-foreground text-sm">Due: {formatDate(task.due_date)}</p>
                                      <p className="font-body mt-1 text-sm">Assigned: {task.assigned_to || 'Unassigned'}</p>
                                      {isStaffOrAdmin && (
                                        <div className="flex gap-2 mt-2 justify-end">
                                          <Button 
                                            size="sm" 
                                            variant="ghost" 
                                            className="h-8 w-8 p-0"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setupTaskForEdit(task);
                                            }}
                                          >
                                            <Pencil className="h-4 w-4" />
                                          </Button>
                                          <Button 
                                            size="sm" 
                                            variant="ghost" 
                                            className="h-8 w-8 p-0 text-destructive hover:text-destructive/90"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeleteTask(task.id);
                                            }}
                                          >
                                            <Trash className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            ))
                          ) : (
                            <div className="text-center p-6 border border-dashed rounded-lg">
                              <p className="text-muted-foreground">No tasks have been created for this implementation yet.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="technical" className="m-0">
                      <div className="p-4 space-y-4">
                        <div>
                          <h3 className="text-sm font-section font-medium text-muted-foreground mb-1">Technical Details</h3>
                          <p className="font-body">{implementationDetails.technical_details || 'No technical details available.'}</p>
                        </div>
                        
                        <div className="bg-gray-900 rounded-lg p-4 text-white font-mono text-sm">
                          <div className="flex items-center justify-between mb-2">
                            <span>Implementation Notes</span>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-7 text-xs text-gray-400 hover:text-white hover:bg-gray-800"
                              onClick={() => {
                                navigator.clipboard.writeText(`# ${implementationDetails.title}
## Technical Implementation Plan

1. **Architecture Overview**
   - Frontend: React with TypeScript
   - Backend: Supabase with RLS policies
   - API: Edge Functions for external integrations

2. **Database Changes**
   - New tables required: ${implementationDetails.id.toLowerCase()}_logs, ${implementationDetails.id.toLowerCase()}_settings
   - Modify existing tables: Add tracking columns

3. **API Endpoints**
   - GET /api/${implementationDetails.id.toLowerCase()}/status
   - POST /api/${implementationDetails.id.toLowerCase()}/process
   - PUT /api/${implementationDetails.id.toLowerCase()}/configure

4. **Testing Strategy**
   - Unit tests for core functions
   - Integration tests for API endpoints
   - E2E tests for user flows

Branch: ${implementationDetails.git_branch || 'N/A'}`);
                                toast.success("Implementation notes copied to clipboard");
                              }}
                            >
                              Copy
                            </Button>
                          </div>
                          <pre className="whitespace-pre-wrap">
{`# ${implementationDetails.title}
## Technical Implementation Plan

1. **Architecture Overview**
   - Frontend: React with TypeScript
   - Backend: Supabase with RLS policies
   - API: Edge Functions for external integrations

2. **Database Changes**
   - New tables required: ${implementationDetails.id.toLowerCase()}_logs, ${implementationDetails.id.toLowerCase()}_settings
   - Modify existing tables: Add tracking columns

3. **API Endpoints**
   - GET /api/${implementationDetails.id.toLowerCase()}/status
   - POST /api/${implementationDetails.id.toLowerCase()}/process
   - PUT /api/${implementationDetails.id.toLowerCase()}/configure

4. **Testing Strategy**
   - Unit tests for core functions
   - Integration tests for API endpoints
   - E2E tests for user flows

Branch: ${implementationDetails.git_branch || 'N/A'}`}
                          </pre>
                        </div>
                      </div>
                    </TabsContent>
                  </CardContent>
                </Tabs>
              </CardHeader>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={isNewImplementationOpen} onOpenChange={setIsNewImplementationOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-title text-xl">Create New Implementation</DialogTitle>
            <DialogDescription>
              Create a new feature implementation plan.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateImplementation} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title" className="font-section">Title</Label>
              <Input 
                id="title" 
                placeholder="Implementation title"
                value={implementationForm.title}
                onChange={(e) => setImplementationForm({...implementationForm, title: e.target.value})}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description" className="font-section">Description</Label>
              <Textarea 
                id="description" 
                placeholder="Description of the implementation"
                value={implementationForm.description}
                onChange={(e) => setImplementationForm({...implementationForm, description: e.target.value})}
                className="min-h-[80px]"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="technical_details" className="font-section">Technical Details</Label>
              <Textarea 
                id="technical_details" 
                placeholder="Technical implementation details"
                value={implementationForm.technical_details}
                onChange={(e) => setImplementationForm({...implementationForm, technical_details: e.target.value})}
                className="min-h-[80px]"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="status" className="font-section">Status</Label>
                <Select 
                  value={implementationForm.status}
                  onValueChange={(value) => setImplementationForm({...implementationForm, status: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planning">Planning</SelectItem>
                    <SelectItem value="in-progress">In Progress</SelectItem>
                    <SelectItem value="testing">Testing</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="blocked">Blocked</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="priority" className="font-section">Priority</Label>
                <Select 
                  value={implementationForm.priority}
                  onValueChange={(value) => setImplementationForm({...implementationForm, priority: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="assigned_to" className="font-section">Assigned Team</Label>
              <Input 
                id="assigned_to" 
                placeholder="Team or individual assigned"
                value={implementationForm.assigned_to}
                onChange={(e) => setImplementationForm({...implementationForm, assigned_to: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="estimated_completion" className="font-section">Estimated Completion</Label>
              <DatePicker 
                date={implementationForm.estimated_completion ? new Date(implementationForm.estimated_completion) : undefined} 
                setDate={(date) => setImplementationForm({
                  ...implementationForm, 
                  estimated_completion: date ? date.toISOString() : ""
                })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="git_branch" className="font-section">Git Branch</Label>
              <Input 
                id="git_branch" 
                placeholder="e.g., feature/feature-name"
                value={implementationForm.git_branch}
                onChange={(e) => setImplementationForm({...implementationForm, git_branch: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="related_request" className="font-section">Related Feature Request</Label>
              <Select 
                value={implementationForm.related_request}
                onValueChange={(value) => setImplementationForm({...implementationForm, related_request: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select related request" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {featureRequests.map(request => (
                    <SelectItem key={request.id} value={request.id}>
                      {request.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <DialogFooter className="mt-6">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  resetImplementationForm();
                  setIsNewImplementationOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-primary"
                disabled={createImplementationMutation.isPending}
              >
                {createImplementationMutation.isPending ? 'Creating...' : 'Create Implementation'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isNewTaskOpen} onOpenChange={setIsNewTaskOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-title text-xl">Add New Task</DialogTitle>
            <DialogDescription>
              Add a new task to this implementation.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateTask} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title" className="font-section">Task Title</Label>
              <Input 
                id="title" 
                placeholder="Task title"
                value={taskForm.title}
                onChange={(e) => setTaskForm({...taskForm, title: e.target.value})}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="status" className="font-section">Status</Label>
              <Select 
                value={taskForm.status}
                onValueChange={(value) => setTaskForm({...taskForm, status: value as ImplementationTask['status']})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not-started">Not Started</SelectItem>
                  <SelectItem value="in-progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="assigned_to" className="font-section">Assigned To</Label>
              <Input 
                id="assigned_to" 
                placeholder="Person or team assigned"
                value={taskForm.assigned_to}
                onChange={(e) => setTaskForm({...taskForm, assigned_to: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="due_date" className="font-section">Due Date</Label>
              <DatePicker 
                date={taskForm.due_date ? new Date(taskForm.due_date) : undefined} 
                setDate={(date) => setTaskForm({
                  ...taskForm, 
                  due_date: date ? date.toISOString() : ""
                })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes" className="font-section">Notes</Label>
              <Textarea 
                id="notes" 
                placeholder="Additional notes about the task"
                value={taskForm.notes}
                onChange={(e) => setTaskForm({...taskForm, notes: e.target.value})}
                className="min-h-[80px]"
              />
            </div>
            
            <DialogFooter className="mt-6">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  resetTaskForm();
                  setIsNewTaskOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-primary"
                disabled={createTaskMutation.isPending}
              >
                {createTaskMutation.isPending ? 'Creating...' : 'Create Task'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditTaskOpen} onOpenChange={setIsEditTaskOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-title text-xl">Edit Task</DialogTitle>
            <DialogDescription>
              Update task details.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleEditTask} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title" className="font-section">Task Title</Label>
              <Input 
                id="edit-title" 
                placeholder="Task title"
                value={taskForm.title}
                onChange={(e) => setTaskForm({...taskForm, title: e.target.value})}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-status" className="font-section">Status</Label>
              <Select 
                value={taskForm.status}
                onValueChange={(value) => setTaskForm({...taskForm, status: value as ImplementationTask['status']})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not-started">Not Started</SelectItem>
                  <SelectItem value="in-progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-assigned_to" className="font-section">Assigned To</Label>
              <Input 
                id="edit-assigned_to" 
                placeholder="Person or team assigned"
                value={taskForm.assigned_to}
                onChange={(e) => setTaskForm({...taskForm, assigned_to: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-due_date" className="font-section">Due Date</Label>
              <DatePicker 
                date={taskForm.due_date ? new Date(taskForm.due_date) : undefined} 
                setDate={(date) => setTaskForm({
                  ...taskForm, 
                  due_date: date ? date.toISOString() : ""
                })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-notes" className="font-section">Notes</Label>
              <Textarea 
                id="edit-notes" 
                placeholder="Additional notes about the task"
                value={taskForm.notes}
                onChange={(e) => setTaskForm({...taskForm, notes: e.target.value})}
                className="min-h-[80px]"
              />
            </div>
            
            <DialogFooter className="mt-6">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setIsEditTaskOpen(false);
                  setSelectedTask(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-primary"
                disabled={updateTaskMutation.isPending}
              >
                {updateTaskMutation.isPending ? 'Updating...' : 'Update Task'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
